import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Alert, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import _ from 'lodash';

import {
  GetSongpopMaestroConfigQuery,
  ItemAvailability,
  LevelRewardInput,
  SongpopMaestroBenefitKind,
  SongpopMaestroPerkKind,
  UpdateSongpopMaestroConfigMutation,
  UpdateSongpopMaestroConfigMutationVariables,
} from '../../../../../../__gqltypes__';
import { SONGPOP_MAESTRO_CONFIG_QUERY, UPDATE_SONGPOP_MAESTRO_CONFIG } from '../graphql';
import Chip from '../../../components/Chip';
import EditRewardsModal from '../../../components/EditRewardsModal';
import { toRewardInput } from '../utils';

type PerkValue = number | boolean | string | null;
type BenefitsSection = 'perks' | 'rewards' | 'ads' | 'display';

// One framed block of the member benefits card, with its own color so each part stands apart.
const SECTION_COLORS = {
  perks: '#0d6efd',
  rewards: '#e67e22',
  ads: '#16a085',
  display: '#6f42c1',
};

function BenefitSection({
  title,
  hint,
  color,
  dirty,
  saving,
  onSave,
  invalid,
  children,
}: {
  title: React.ReactNode;
  hint?: string;
  color: string;
  // its own Save, at the bottom right: enabled once something of this section changed
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  // why it cannot be saved as it is: shown next to the Save button, which stays disabled
  invalid?: string | null;
  children?: React.ReactNode;
}) {
  return (
    <div className="rounded mb-3 overflow-hidden" style={{ border: `1px solid ${color}`, borderLeftWidth: 5 }}>
      <div className="px-3 py-1" style={{ backgroundColor: `${color}1f`, color }}>
        <strong>{title}</strong>
        {hint && <small className="ml-2 text-muted">{hint}</small>}
      </div>
      <div className="px-3 py-2">
        {children}
        <div className="d-flex justify-content-end align-items-center mt-2">
          {invalid && <small className="text-danger mr-2">{invalid}</small>}
          <Button
            size="sm"
            variant={dirty && !invalid ? 'success' : 'outline-secondary'}
            disabled={saving || !dirty || !!invalid}
            title={invalid || (dirty ? '' : 'Nothing changed')}
            onClick={onSave}
          >
            {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Colors of the perk groups inside the lifetime perks section.
const PERK_GROUP_COLORS: Record<string, string> = {
  'Every day': '#0d6efd',
  'Game modes': '#d63384',
  'Playlists & ads': '#198754',
  'Offer display': '#fd7e14',
  Other: '#6c757d',
};

// Rewarded ad zones (app zoneId) the noRewardedAds perk can skip, edited in their own card.
const REWARDED_AD_ZONES: Array<{ zone: string; label: string }> = [
  { zone: 'free_currency_for_ads', label: 'Shop free coin / diamond cards' },
  { zone: 'wheel_fortune_watch_ad', label: 'Wheel of Fortune spin' },
  { zone: 'chained_offer_watch_ad', label: 'Chained offer ad step' },
  { zone: 'live_event_refill_ticket', label: 'Live Event ticket refill' },
  { zone: 'claim_mission', label: 'Monthly Pass / Special Event mission claim' },
  { zone: 'tuneology', label: 'Hurdle / Tuneology reward multiplier' },
  { zone: 'playlist_selection', label: 'Playlist selection (ad or VIP)' },
  { zone: 'daily_bonus_claimx2', label: 'Daily bonus (stamp card) claim x2' },
  { zone: 'daily_bonus_revive', label: 'Daily bonus (stamp card) revive' },
  { zone: 'progressive_rewarded_ads', label: 'PRA (progressive rewarded ads)' },
];
const REWARDED_AD_PERKS = new Set(['noRewardedAds', 'noRewardedAdZones']);
// The rewarded ads card is hidden for now (kept, not removed): true shows it again. Hidden, the
// stored noRewardedAds / noRewardedAdZones stay as they are and still apply.
const SHOW_REWARDED_ADS_SECTION = false;
// Perks not offered here: the app no longer shows the loyalty bonus percent (the Fame line shows the
// amount, as on Songpop Plus). The stored value is kept as is.
const HIDDEN_PERKS = new Set(['loyaltyPointsBonusPercent']);

// How each perk key (the config / DB name stays unchanged) is shown: its group, a readable name
// and what the number means.
type PerkDisplay = { group: string; label: string; unit?: string };
const PERK_DISPLAY: Record<string, PerkDisplay> = {
  dailyDiamonds: { group: 'Every day', label: 'Free diamonds', unit: 'diamonds (0 = none)' },
  dailyFreeSpins: {
    group: 'Every day',
    label: 'Extra Wheel spins',
    unit: 'per wheel refresh (24 h), on top of the tier ones, no ad',
  },
  liveShowFreeEntriesPerWeek: { group: 'Game modes', label: 'Live Show free entries', unit: 'per week' },
  marathonFreeStartsPerEvent: {
    group: 'Game modes',
    label: 'Marathon free starts',
    unit: 'per event (-1 = unlimited)',
  },
  playlistUpgradeWaiveCoins: { group: 'Playlists & ads', label: 'Playlist upgrade without coins' },
  playlistUpgradeWaiveXp: { group: 'Playlists & ads', label: 'Playlist upgrade without XP' },
  noInterstitialAds: {
    group: 'Playlists & ads',
    label: 'No interstitial ads',
    unit: 'the full-screen ads between rounds; rewarded ads stay',
  },
  loyaltyPointsBonusPercent: {
    group: 'Offer display',
    label: 'Loyalty bonus shown',
    unit: '% (text only, 0 = hidden)',
  },
};
const PERK_GROUPS = ['Every day', 'Game modes', 'Playlists & ads', 'Offer display', 'Other'];

// The lines of the benefit screen and shop card, by order key (SongpopMaestroOffer.benefitOrder).
// `kind`: the benefit the line's switch hides (none: always shown when it has something).
// `active`: the perk gives something with these values; an inactive one is never listed anyway.
// NO_REWARDED_ADS is not offered here (its perk card is hidden): it keeps its server place.
type BenefitLine = {
  key: string;
  label: string;
  kind?: SongpopMaestroBenefitKind;
  active?: (perks: Record<string, PerkValue>) => boolean;
};
const BENEFIT_LINES: BenefitLine[] = [
  {
    key: 'INSTANT_CURRENCIES',
    label: 'Exclusive rewards: currencies ("Earn ... instantly"), still granted when hidden',
    kind: SongpopMaestroBenefitKind.INSTANT_CURRENCIES,
  },
  {
    key: 'DAILY_DIAMONDS',
    label: 'Free diamonds every day',
    kind: SongpopMaestroBenefitKind.DAILY_DIAMONDS,
    active: (perks) => Number(perks.dailyDiamonds) > 0,
  },
  {
    key: 'EXCLUSIVE_ITEMS',
    label: 'Exclusive rewards: vanity items, still granted when hidden',
    kind: SongpopMaestroBenefitKind.EXCLUSIVE_ITEMS,
  },
  {
    key: 'DAILY_CHEST',
    label: 'Free daily Maestro chest (given by another feature)',
    kind: SongpopMaestroBenefitKind.DAILY_CHEST,
  },
  {
    key: 'DAILY_FREE_SPINS',
    label: 'Extra Wheel spins',
    kind: SongpopMaestroBenefitKind.DAILY_FREE_SPINS,
    active: (perks) => Number(perks.dailyFreeSpins) > 0,
  },
  {
    key: 'LIVE_SHOW_FREE_ENTRIES',
    label: 'Live Show free entries',
    kind: SongpopMaestroBenefitKind.LIVE_SHOW_FREE_ENTRIES,
    active: (perks) => Number(perks.liveShowFreeEntriesPerWeek) > 0,
  },
  {
    key: 'MARATHON_FREE_STARTS',
    label: 'Marathon free starts',
    kind: SongpopMaestroBenefitKind.MARATHON_FREE_STARTS,
    active: (perks) => Number(perks.marathonFreeStartsPerEvent) !== 0,
  },
  {
    key: 'NO_INTERSTITIAL_ADS',
    label: 'No interstitial ads',
    kind: SongpopMaestroBenefitKind.NO_INTERSTITIAL_ADS,
    active: (perks) => !!perks.noInterstitialAds,
  },
  {
    key: 'FREE_PLAYLIST_UPGRADES',
    label: 'Playlist upgrades without coins',
    kind: SongpopMaestroBenefitKind.FREE_PLAYLIST_UPGRADES,
    active: (perks) => !!perks.playlistUpgradeWaiveCoins,
  },
  {
    key: 'FAME',
    label: 'Fame of the purchase (shown when the SKU has a loyalty config amount)',
  },
];
const BENEFIT_LINE_KEYS = BENEFIT_LINES.map(({ key }) => key);
// The stored order restricted to the lines edited here (the server completes the others).
const linesOrderOf = (order: string[]) => order.filter((key) => BENEFIT_LINE_KEYS.includes(key));

function zonesOf(value: PerkValue) {
  return String(value ?? '')
    .split(',')
    .map((zone) => zone.trim())
    .filter(Boolean);
}


/**
 * Perk config (GameConfig features.songpopMaestro): who is offered the pack (kids mode / the
 * others), the perks kill switch and every perk value (the feature switch lives in the page
 * header). One record for beta and prod, cached up to 5 minutes by the backend, and perk changes
 * apply retroactively to every owner.
 */
const CONFIRM_TEXTS = {
  kidsOn: {
    title: 'Offer the pack to kids mode?',
    body: 'Kids mode accounts start seeing the offer, the shop card and the pushes. It can take a few minutes to show in the app.',
    button: 'Offer to kids mode',
  },
  kidsOff: {
    title: 'Stop offering the pack to kids mode?',
    body: 'Kids mode accounts stop seeing the offer, the shop card and the pushes. Kids who already own the pack keep every perk.',
    button: 'Stop for kids mode',
  },
  nonKidsOn: {
    title: 'Offer the pack to non-kids accounts?',
    body: 'Every other account starts seeing the offer, the shop card and the pushes. It can take a few minutes to show in the app.',
    button: 'Offer to non-kids',
  },
  nonKidsOff: {
    title: 'Stop offering the pack to non-kids accounts?',
    body: 'Non-kids accounts stop seeing the offer, the shop card and the pushes. Players who already own the pack keep every perk.',
    button: 'Stop for non-kids',
  },
  perksOn: {
    title: 'Turn the perks on?',
    body: 'Every owner gets the perks below back (only while the Songpop Maestro switch at the top is ON). It can take a few minutes to show in the app.',
    button: 'Turn perks on',
  },
  perksOff: {
    title: 'Turn every perk off?',
    body: 'Every owner - paying players included - loses all the perks below until this is turned back on. The Maestro leaderboards, the Founders wall and the badges stay. Use it for an exploit only.',
    button: 'Turn perks off',
  },
  wallOn: {
    title: 'Show the Founders wall?',
    body: 'Every player sees the Founders wall and the founder badges again. The numbers did not change. It can take a few minutes to show in the app.',
    button: 'Show the wall',
  },
  wallOff: {
    title: 'Hide the Founders wall?',
    body: 'The wall and the Songpop Maestro card of every profile disappear. Every number is kept and comes back when turned on.',
    button: 'Hide the wall',
  },
  wallKidsOn: {
    title: 'Show kids mode owners on the Founders wall?',
    body: 'Kids mode owners appear on the wall (name and picture) and their founder badge is shown to other players.',
    button: 'Show kids',
  },
  wallKidsOff: {
    title: 'Leave kids mode owners out of the Founders wall?',
    body: 'Kids mode owners disappear from the wall and other players stop seeing their founder badge. They keep their number and still see it.',
    button: 'Leave kids out',
  },
  savePerks: { title: 'Save the lifetime perks?', body: '', button: 'Save' },
  saveRewards: { title: 'Save the exclusive rewards?', body: '', button: 'Save' },
  saveAds: { title: 'Save the rewarded ads?', body: '', button: 'Save' },
  saveDisplay: {
    title: 'Save the benefit screen?',
    body: 'Only what the offer lists changes (benefit screen, shop card). Every perk keeps working. It can take a few minutes to show in the app.',
    button: 'Save',
  },
};

/**
 * Parts of the Maestro config (GameConfig features.songpopMaestro), each placed on the single
 * Maestro page where it belongs:
 * - audience: kids / non-kids, with the event;
 * - benefits: the perks kill switch on top, then lifetime perks, exclusive rewards and rewarded ad
 *   zones of every owner, saved together;
 * - foundersWall: the wall switches.
 * Every part reads the same query (refetched after a save, so the others follow).
 */
export function MaestroPackSettings({ section }: { section: 'audience' | 'benefits' | 'foundersWall' }) {
  const { data, loading, refetch } = useQuery<GetSongpopMaestroConfigQuery>(SONGPOP_MAESTRO_CONFIG_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateConfig, { loading: saving, error }] = useMutation<
    UpdateSongpopMaestroConfigMutation,
    UpdateSongpopMaestroConfigMutationVariables
  >(UPDATE_SONGPOP_MAESTRO_CONFIG, { onCompleted: () => refetch() });

  const [perks, setPerks] = useState<Record<string, PerkValue>>({});
  const [rewards, setRewards] = useState<LevelRewardInput[]>([]);
  // null: adding a reward, else the index of the edited one
  const [editedRewardIndex, setEditedRewardIndex] = useState<number | null>(null);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [showAdZones, setShowAdZones] = useState(false);
  const [hiddenBenefits, setHiddenBenefits] = useState<SongpopMaestroBenefitKind[]>([]);
  const [benefitOrder, setBenefitOrder] = useState<string[]>([]);
  // A switch (audience, perks, wall) saves on its own once confirmed; 'save*': one benefits section.
  const [confirm, setConfirm] = useState<null | keyof typeof CONFIRM_TEXTS>(null);

  const config = data?.liveOps.songpopMaestroConfig;
  const perkFields = data?.liveOps.songpopMaestroPerkFields ?? [];
  const storedRewards = (config?.instantRewards ?? []).map(toRewardInput);

  // After a save the config is refetched: only the saved section (all of them on the first load)
  // takes the stored values, so the unsaved edits of the other sections stay.
  const loadedRef = useRef(false);
  const savedSectionRef = useRef<BenefitsSection | null>(null);
  useEffect(() => {
    if (!config) {
      return;
    }
    const all = !loadedRef.current;
    const saved = savedSectionRef.current;
    loadedRef.current = true;
    savedSectionRef.current = null;
    const stored = (config.perks ?? {}) as Record<string, PerkValue>;
    if (all) {
      setPerks(stored);
    } else if (saved === 'perks' || saved === 'ads') {
      // perks and rewarded ads share the perks map: only the saved keys are reset
      setPerks((previous) => ({
        ...previous,
        ..._.pickBy(stored, (__, key) => REWARDED_AD_PERKS.has(key) === (saved === 'ads')),
      }));
    }
    if (all || saved === 'rewards') {
      setRewards(config.instantRewards.map(toRewardInput));
    }
    if (all || saved === 'display') {
      setHiddenBenefits(config.hiddenBenefits);
      setBenefitOrder(linesOrderOf(config.benefitOrder));
    }
  }, [config]);

  if (!config) {
    return loading ? <Spinner animation="border" size="sm" /> : null;
  }

  // Fields left out keep their stored value: `enabled` belongs to the header switch.
  const send = (input: UpdateSongpopMaestroConfigMutationVariables['input']) =>
    updateConfig({ variables: { input } }).catch(() => {
      // surfaced through error; nothing was saved, so no section takes the stored values
      savedSectionRef.current = null;
    });

  const storedPerks = (config.perks ?? {}) as Record<string, PerkValue>;
  const perksOf = (values: Record<string, PerkValue>, ads: boolean) =>
    _.pickBy(values, (__, key) => REWARDED_AD_PERKS.has(key) === ads);
  const dirty: Record<BenefitsSection, boolean> = {
    perks: !_.isEqual(perksOf(perks, false), perksOf(storedPerks, false)),
    ads: !_.isEqual(perksOf(perks, true), perksOf(storedPerks, true)),
    rewards: !_.isEqual(rewards, storedRewards),
    display:
      !_.isEqual(_.sortBy(hiddenBenefits), _.sortBy(config.hiddenBenefits)) ||
      !_.isEqual(benefitOrder, linesOrderOf(config.benefitOrder)),
  };
  // only the known fields: unknown ones stay as stored on the backend. A field set to null (an
  // emptied text, no rewarded ad zone) is sent as null, not reset to its default.
  const perksInput = (ads: boolean) =>
    Object.fromEntries(
      perkFields
        .filter((field) => REWARDED_AD_PERKS.has(field.key) === ads)
        .map((field) => [field.key, field.key in perks ? perks[field.key] : field.default])
    );
  const saveSection = (saved: BenefitsSection, input: UpdateSongpopMaestroConfigMutationVariables['input']) => {
    savedSectionRef.current = saved;
    send(input);
  };

  const confirmAction = () => {
    const action = confirm;
    setConfirm(null);
    if (action === 'kidsOn' || action === 'kidsOff') {
      send({ availableForKids: action === 'kidsOn' });
      return;
    }
    if (action === 'nonKidsOn' || action === 'nonKidsOff') {
      send({ availableForNonKids: action === 'nonKidsOn' });
      return;
    }
    if (action === 'wallOn' || action === 'wallOff') {
      send({ foundersWallEnabled: action === 'wallOn' });
      return;
    }
    if (action === 'wallKidsOn' || action === 'wallKidsOff') {
      send({ foundersWallShowKids: action === 'wallKidsOn' });
      return;
    }
    if (action === 'perksOn' || action === 'perksOff') {
      send({ perksEnabled: action === 'perksOn' });
      return;
    }
    if (action === 'savePerks') {
      saveSection('perks', { perks: perksInput(false) });
    } else if (action === 'saveAds') {
      saveSection('ads', { perks: perksInput(true) });
    } else if (action === 'saveDisplay') {
      saveSection('display', { hiddenBenefits, benefitOrder });
    } else if (action === 'saveRewards') {
      saveSection('rewards', {
        instantRewards: rewards.map(({ type, amount, itemId }) => ({ type, amount, itemId: itemId ?? undefined })),
      });
    }
  };

  const openRewardModal = (index: number | null) => {
    setEditedRewardIndex(index);
    setShowRewardModal(true);
  };
  const handleSaveReward = (reward: LevelRewardInput) => {
    setRewards((previous) =>
      editedRewardIndex === null
        ? [...previous, reward]
        : previous.map((current, index) => (index === editedRewardIndex ? reward : current))
    );
    setShowRewardModal(false);
  };

  const perksEnabled = config.perksEnabled;

  const confirmModal = (
    <Modal show={confirm !== null} onHide={() => setConfirm(null)}>
      <Modal.Header closeButton>
        <Modal.Title>{CONFIRM_TEXTS[confirm ?? 'savePerks'].title}</Modal.Title>
      </Modal.Header>
      {!!CONFIRM_TEXTS[confirm ?? 'savePerks'].body && (
        <Modal.Body>{CONFIRM_TEXTS[confirm ?? 'savePerks'].body}</Modal.Body>
      )}
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setConfirm(null)}>
          Cancel
        </Button>
        <Button
          variant={confirm === 'perksOff' || confirm === 'kidsOff' || confirm === 'nonKidsOff' ? 'danger' : 'success'}
          onClick={confirmAction}
        >
          {CONFIRM_TEXTS[confirm ?? 'savePerks'].button}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  if (section === 'audience') {
    return (
      <>
        {error && <Alert variant="danger">{error.message}</Alert>}
        <Row className="mb-3 align-items-center">
          <Col md={3}>Audience (every event):</Col>
          <Col md={9}>
            <Form.Check
              inline
              type="switch"
              id="maestro-available-for-non-kids"
              label="Non-kids accounts"
              checked={config.availableForNonKids}
              disabled={saving}
              onChange={(e) => setConfirm(e.target.checked ? 'nonKidsOn' : 'nonKidsOff')}
            />
            <Form.Check
              inline
              type="switch"
              id="maestro-available-for-kids"
              label="Kids mode"
              checked={config.availableForKids}
              disabled={saving}
              onChange={(e) => setConfirm(e.target.checked ? 'kidsOn' : 'kidsOff')}
            />
            <Form.Text className="text-muted">
              Who is offered the pack (offer, shop card, pushes), on top of the event&apos;s matchers below. Saved right
              away. Owners keep their perks whatever these say.
            </Form.Text>
          </Col>
        </Row>
        {confirmModal}
      </>
    );
  }

  if (section === 'foundersWall') {
    return (
      <Card className="mb-3">
        <Card.Header>Founders wall</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger">{error.message}</Alert>}
          <Form.Check
            type="switch"
            id="maestro-founders-wall-enabled"
            label="Founders wall enabled"
            checked={config.foundersWallEnabled}
            disabled={saving}
            onChange={(e) => setConfirm(e.target.checked ? 'wallOn' : 'wallOff')}
          />
          <Form.Check
            type="switch"
            id="maestro-founders-wall-show-kids"
            label="Show kids mode owners"
            checked={config.foundersWallShowKids}
            disabled={saving || !config.foundersWallEnabled}
            onChange={(e) => setConfirm(e.target.checked ? 'wallKidsOn' : 'wallKidsOff')}
          />
          {confirmModal}
        </Card.Body>
      </Card>
    );
  }

  // benefits
  return (
    <Card className="mb-3">
      <Card.Header>Member benefits (every owner, for life)</Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error.message}</Alert>}
        <div className="mb-3">
          <Form.Check
            type="switch"
            id="maestro-perks-enabled"
            label={
              perksEnabled ? (
                <strong>Perks ON: every owner gets the perks below</strong>
              ) : (
                <strong className="text-danger">Perks OFF: no owner gets any of the perks below</strong>
              )
            }
            checked={perksEnabled}
            disabled={saving}
            onChange={(e) => setConfirm(e.target.checked ? 'perksOn' : 'perksOff')}
          />
          <Form.Text className="text-muted">
            Emergency switch for an exploit: stops the perks only (daily diamonds, spins, Live Show, Marathon,
            playlists, ads). The Maestro leaderboards, the Founders wall and the badges stay. To rebalance a perk, lower
            its value instead. Only works while the Songpop Maestro switch at the top is ON.
          </Form.Text>
        </div>
        <BenefitSection
          title="Lifetime perks"
          hint="retroactive: raise a value, never lower it"
          color={SECTION_COLORS.perks}
          dirty={dirty.perks}
          saving={saving}
          onSave={() => setConfirm('savePerks')}
        >
          <Row>
            {PERK_GROUPS.map((group) => {
              const fields = perkFields.filter(
                (field) =>
                  !REWARDED_AD_PERKS.has(field.key) &&
                  !HIDDEN_PERKS.has(field.key) &&
                  (PERK_DISPLAY[field.key]?.group ?? 'Other') === group
              );
              if (fields.length === 0) {
                return null;
              }
              return (
                <Col key={group} lg={6} xl={3} className="mb-2">
                  <div
                    className="text-uppercase small font-weight-bold mb-1 pb-1"
                    style={{ color: PERK_GROUP_COLORS[group], borderBottom: `2px solid ${PERK_GROUP_COLORS[group]}` }}
                  >
                    {group}
                  </div>
                  {fields.map((field) => {
                    const display = PERK_DISPLAY[field.key];
                    const defaultText =
                      field.kind === SongpopMaestroPerkKind.BOOL
                        ? `default ${field.default ? 'on' : 'off'}`
                        : `default ${
                            field.default === -1 && field.allowUnlimited ? 'unlimited' : String(field.default)
                          }`;
                    // differs from the default: highlighted, so a changed value stands out
                    const changed = !_.isEqual(perks[field.key], field.default);
                    const note = (
                      <small className={`d-block ${changed ? 'text-warning' : 'text-muted'}`}>
                        {[display?.unit, defaultText].filter(Boolean).join(' · ')}
                      </small>
                    );
                    const tooltip = [
                      field.key,
                      field.min !== null && field.min !== undefined ? `min ${field.min}` : '',
                      field.max !== null && field.max !== undefined ? `max ${field.max}` : '',
                    ]
                      .filter(Boolean)
                      .join(', ');
                    return (
                      <div key={field.key} className="d-flex align-items-center py-1" title={tooltip}>
                        {field.kind === SongpopMaestroPerkKind.BOOL ? (
                          <Form.Check
                            type="switch"
                            id={`maestro-perk-${field.key}`}
                            label={
                              <>
                                {display?.label ?? field.key}
                                {note}
                              </>
                            }
                            checked={!!perks[field.key]}
                            onChange={(e) => {
                              // Read before the updater: React may have cleared the event when it runs.
                              const { checked } = e.target;
                              setPerks((previous) => ({ ...previous, [field.key]: checked }));
                            }}
                          />
                        ) : (
                          <>
                            <Form.Control
                              size="sm"
                              className="mr-2 flex-shrink-0"
                              style={{ width: 70 }}
                              type={field.kind === SongpopMaestroPerkKind.INT ? 'number' : 'text'}
                              value={(perks[field.key] as string | number | null) ?? ''}
                              onChange={(e) => {
                                // Read before the updater: React may have cleared the event when it runs.
                                const { value } = e.target;
                                setPerks((previous) => ({
                                  ...previous,
                                  [field.key]:
                                    field.kind === SongpopMaestroPerkKind.INT ? Number(value) : value.trim() || null,
                                }));
                              }}
                            />
                            <span>
                              {display?.label ?? field.key}
                              {note}
                            </span>
                          </>
                        )}
                      </div>
                    );
                  })}
                </Col>
              );
            })}
          </Row>
        </BenefitSection>

        <BenefitSection
          title="Exclusive rewards"
          hint="once, right after the purchase or an admin grant"
          color={SECTION_COLORS.rewards}
          dirty={dirty.rewards}
          saving={saving}
          onSave={() => setConfirm('saveRewards')}
          invalid={rewards.length === 0 ? 'Add at least one reward.' : null}
        >
          <div className="d-flex flex-wrap">
            {rewards.map((reward, index) => (
              <Chip
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                reward={reward}
                canDelete
                btnAction={() => setRewards((previous) => previous.filter((__, i) => i !== index))}
                globalAction={() => openRewardModal(index)}
              />
            ))}
            <Chip isAdd btnAction={() => openRewardModal(null)} globalAction={() => openRewardModal(null)} />
          </div>
        </BenefitSection>

        <BenefitSection
          title="Benefit screen"
          hint="which lines the offer shows and in what order (benefit screen, shop card); display only, the perks keep working"
          color={SECTION_COLORS.display}
          dirty={dirty.display}
          saving={saving}
          onSave={() => setConfirm('saveDisplay')}
        >
          <small className="d-block text-muted mb-2">Top to bottom = the order players see.</small>
          {benefitOrder.map((key, index) => {
            const line = BENEFIT_LINES.find((candidate) => candidate.key === key);
            if (!line) {
              return null;
            }
            const { kind, label, active } = line;
            const move = (offset: number) =>
              setBenefitOrder((previous) => {
                const next = [...previous];
                [next[index], next[index + offset]] = [next[index + offset], next[index]];
                return next;
              });
            return (
              <div key={key} className="d-flex align-items-center py-1 border-bottom">
                <span className="text-muted mr-2" style={{ width: 20 }}>
                  {index + 1}.
                </span>
                <Button variant="link" size="sm" className="p-0 mr-1" disabled={index === 0} onClick={() => move(-1)}>
                  <i className="fas fa-arrow-up" />
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 mr-2"
                  disabled={index === benefitOrder.length - 1}
                  onClick={() => move(1)}
                >
                  <i className="fas fa-arrow-down" />
                </Button>
                <span className="flex-grow-1">
                  {label}
                  {active && !active(storedPerks) && (
                    <small className="ml-2 text-muted">not listed anyway: the perk is off / 0</small>
                  )}
                </span>
                {kind && (
                  <Form.Check
                    type="switch"
                    id={`maestro-benefit-${kind}`}
                    label="shown"
                    checked={!hiddenBenefits.includes(kind)}
                    onChange={(e) => {
                      // Read before the updater: React may have cleared the event when it runs.
                      const { checked } = e.target;
                      setHiddenBenefits((previous) =>
                        checked ? previous.filter((other) => other !== kind) : [...previous, kind]
                      );
                    }}
                  />
                )}
              </div>
            );
          })}
        </BenefitSection>

        {SHOW_REWARDED_ADS_SECTION && (
        <BenefitSection
          color={SECTION_COLORS.ads}
          dirty={dirty.ads}
          saving={saving}
          onSave={() => setConfirm('saveAds')}
          title={
            <Button
              variant="link"
              className="p-0 font-weight-bold"
              style={{ color: 'inherit' }}
              onClick={() => setShowAdZones((shown) => !shown)}
            >
              <i className={`fas fa-chevron-${showAdZones ? 'down' : 'right'} mr-2`} />
              Rewarded ads
            </Button>
          }
        >
          {showAdZones && (
            <>
              <Form.Check
                type="switch"
                id="maestro-perk-noRewardedAds"
                label="Owners get the reward without watching the ad (noRewardedAds)"
                checked={!!perks.noRewardedAds}
                onChange={(e) => {
                  const { checked } = e.target;
                  setPerks((previous) => ({ ...previous, noRewardedAds: checked }));
                }}
              />
              <p className="text-muted mt-2">
                Off by default: owners stop bringing rewarded ad revenue where it is on. The reward limits of each
                feature stay. Interstitial ads are a separate perk (noInterstitialAds).
              </p>
              {!!perks.noRewardedAds &&
                REWARDED_AD_ZONES.map(({ zone, label }) => (
                  <Form.Check
                    key={zone}
                    type="checkbox"
                    id={`maestro-rewarded-zone-${zone}`}
                    label={
                      <>
                        {label} <code>{zone}</code>
                      </>
                    }
                    checked={zonesOf(perks.noRewardedAdZones).includes(zone)}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setPerks((previous) => {
                        const zones = zonesOf(previous.noRewardedAdZones).filter((other) => other !== zone);
                        return { ...previous, noRewardedAdZones: (checked ? [...zones, zone] : zones).join(',') };
                      });
                    }}
                  />
                ))}
            </>
          )}
        </BenefitSection>
        )}

        {confirmModal}
        <EditRewardsModal
          isEdit={editedRewardIndex !== null}
          tier={null}
          level={0}
          show={showRewardModal}
          reward={editedRewardIndex !== null ? rewards[editedRewardIndex] : null}
          handleSave={handleSaveReward}
          handleClose={() => setShowRewardModal(false)}
          disableCurrencySpecialEvent
          disableMonthlyBuff
          enableItemLevelEdit
          liveOpsAvailabilities={[ItemAvailability.EARN, ItemAvailability.EARN_EACH_LEVEL]}
        />
      </Card.Body>
    </Card>
  );
}
